import React, { useEffect, useState } from 'react';
import { 
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import AccountDetailsDialog from './AccountDetailsDialog';
import LoanDetailsDialog from './LoanDetailsDialog';
import ManagerCard from './ManagerCard';
import CollectionOfficerCard from './CollectionOfficerCard';
import TotalPortfolioCard from './TotalPortfolioCard';
import UnassignedAccountsDialog, { UnassignedAccount } from './UnassignedAccountsDialog';
import apiClient, { getErrorMessage, createApiClient } from '../utils/api';
// Debug components removed - profile pictures are now working!
import './PortHealth.css';

interface PortfolioHealthMetrics {
  totalLoans: number;
  currentLoans: number;
  pastDueLoans: number;
  oneTo10: number;
  elevenTo30: number;
  thirtyOneTo60: number;
  sixtyOneTo90: number;
  ninetyOneTo120: number;
  oneTwentyOnePlus: number;
  thirtyPlus: number;
  pctCurrent: number;
  pctPastDue: number;
  pctOneTo10: number;
  pctElevenTo30: number;
  pctThirtyOneTo60: number;
  pctSixtyOneTo90: number;
  pctNinetyOneTo120: number;
  pctOneTwentyOnePlus: number;
  pctThirtyPlus: number;
  totalBalance: number;
  trends?: {
    oneTo10: 'green' | 'orange' | 'red';
    elevenTo30: 'green' | 'orange' | 'red';
    thirtyOneTo60: 'green' | 'orange' | 'red';
    sixtyOneTo90: 'green' | 'orange' | 'red';
    ninetyOneTo120: 'green' | 'orange' | 'red';
    oneTwentyOnePlus: 'green' | 'orange' | 'red';
    thirtyPlus: 'green' | 'orange' | 'red';
  };
}

interface AccountDetail {
  acctrefno: number;
  loan_number: string;
  customer_name: string;
  email_address: string;
  current_principal_balance: number;
  current_interest_balance: number;
  current_payoff_balance: number;
  days_past_due: number;
  last_extension_date: string;
  deferment_count: number;
  last_promise_date: string;
  last_promise_amount: string;
  last_payment_date: string;
  last_payment_amount: string;
  account_manager: string;
  // cease_and_desist: string;
  loan_group: string;
}

interface PaginationInfo {
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

interface SortingInfo {
  sort_by: string;
  sort_order: string;
}

interface LoanDetail {
  acctrefno: number;
  loan_number: string;
  customer_name: string;
  email_address: string;
  principal_balance: number;
  interest_balance: number;
  payoff_balance: number;
  days_past_due: number;
  last_extension_date: string;
  deferment_count: number;
  last_promise_date: string;
  last_promise_amount: string;
  last_payment_date: string;
  last_payment_amount: string;
  account_manager: string;
  // cease_and_desist: string;
  loan_group: string;
}

interface Manager {
  userno: number;
  username: string;
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  full_name: string;
}

interface CollectionOfficer {
  userno: number;
  username: string;
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  full_name: string;
}

interface ManagerMetrics {
  total_loans: number;
  current_loans: number;
  past_due_loans: number;
  one_to_10: number;
  eleven_to_30: number;
  thirty_one_to_60: number;
  sixty_one_to_90: number;
  ninety_one_to_120: number;
  one_twenty_one_plus: number;
  thirty_plus: number;
  total_balance: number;
  pct_current: number;
  pct_past_due: number;
  pct_one_to_10: number;
  pct_eleven_to_30: number;
  pct_thirty_one_to_60: number;
  pct_sixty_one_to_90: number;
  pct_ninety_one_to_120: number;
  pct_one_twenty_one_plus: number;
  pct_thirty_plus: number;
}

interface UnassignedSummary {
  total_unassigned: number;
  yes_welcome_call: number;
  no_welcome_call: number;
}

const PortHealth: React.FC = () => {
  const [metrics, setMetrics] = useState<PortfolioHealthMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentAccounts, setCurrentAccounts] = useState<AccountDetail[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [dialogType, setDialogType] = useState<'current' | 'past-due' | 'bucket'>('current');
  const [bucketInfo, setBucketInfo] = useState<{minDays: number, maxDays?: number, name: string} | null>(null);
  const [isManagerAccounts, setIsManagerAccounts] = useState(false);
  const [accountTotals, setAccountTotals] = useState<{total_principal_balance: number, total_payoff_balance: number, total_balance: number} | null>(null);
  
  // Pagination state
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [sorting, setSorting] = useState<SortingInfo>({ sort_by: 'days_past_due', sort_order: 'DESC' });
  const [currentPage, setCurrentPage] = useState(1);
  
  // Search state
  const [searchLoanNumber, setSearchLoanNumber] = useState('');
  const [searchCustomerName, setSearchCustomerName] = useState('');
  const [isSearchPending, setIsSearchPending] = useState(false);
  
  // Ref to store timeout ID for cleanup
  const searchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const [loanDetailsDialogOpen, setLoanDetailsDialogOpen] = useState(false);
  const [loanDetails, setLoanDetails] = useState<LoanDetail | null>(null);
  const [loadingLoanDetails, setLoadingLoanDetails] = useState(false);
  const [loanDetailsError, setLoanDetailsError] = useState<string | null>(null);
  
  // Loading cursor state
  const [isLoadingCursor, setIsLoadingCursor] = useState(false);
  
  // Manager/Officer view state
  const [viewMode, setViewMode] = useState<'overview' | 'managers' | 'officers'>('overview');
  const [managers, setManagers] = useState<Manager[]>([]);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [selectedManager, setSelectedManager] = useState<Manager | null>(null);
  const [officers, setOfficers] = useState<CollectionOfficer[]>([]);
  const [loadingOfficers, setLoadingOfficers] = useState(false);
  const [selectedOfficer, setSelectedOfficer] = useState<CollectionOfficer | null>(null);
  const [managerMetrics, setManagerMetrics] = useState<{[key: number]: ManagerMetrics}>({});
  const [officerMetrics, setOfficerMetrics] = useState<{[key: number]: ManagerMetrics}>({});
  const [unassignedSummary, setUnassignedSummary] = useState<UnassignedSummary | null>(null);
  const [loadingUnassigned, setLoadingUnassigned] = useState(false);
  const [unassignedAccounts, setUnassignedAccounts] = useState<UnassignedAccount[]>([]);
  const [unassignedDialogOpen, setUnassignedDialogOpen] = useState(false);

  useEffect(() => {
    fetchPortfolioHealth();
    fetchUnassignedSummary();
  }, []);

  // Manage loading cursor based on various loading states
  useEffect(() => {
    const shouldShowLoadingCursor = loadingAccounts || loadingLoanDetails || loadingManagers || loadingOfficers;
    setIsLoadingCursor(shouldShowLoadingCursor);
  }, [loadingAccounts, loadingLoanDetails, loadingManagers, loadingOfficers]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);



  const fetchPortfolioHealth = async () => {
    // Also refresh unassigned accounts summary when refreshing portfolio health
    fetchUnassignedSummary();
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/port-health/metrics');
      setMetrics(response.data);
    } catch (err: any) {
      console.error('Error fetching portfolio health:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchUnassignedSummary = async () => {
    try {
      setLoadingUnassigned(true);
      const response = await apiClient.get('/port-health/unassigned-summary');
      const data = response.data;
      setUnassignedSummary({
        total_unassigned: data.total_unassigned ?? 0,
        yes_welcome_call: data.yes_welcome_call ?? 0,
        no_welcome_call: data.no_welcome_call ?? 0
      });
    } catch (err) {
      console.error('Error fetching unassigned accounts summary:', err);
    } finally {
      setLoadingUnassigned(false);
    }
  };

  const fetchCurrentAccountsDetails = async (page: number = 1, sortBy: string = 'days_past_due', sortOrder: string = 'ASC', searchLoan: string = '', searchCustomer: string = '') => {
    try {
      setLoadingAccounts(true);
      setError(null);
      setDialogType('current');
      
      const params: any = {
        page: page.toString(),
        page_size: '25',
        sort_by: sortBy,
        sort_order: sortOrder
      };
      if (searchLoan) params.search_loan_number = searchLoan;
      if (searchCustomer) params.search_customer_name = searchCustomer;
      
      const response = await apiClient.get('/port-health/current-accounts', { params });
      const data = response.data;
      
      setCurrentAccounts(data.accounts || []);
      setPagination(data.pagination || null);
      setSorting(data.sorting || { sort_by: 'days_past_due', sort_order: 'ASC' });
      setCurrentPage(page);
      setIsManagerAccounts(false); // Reset manager accounts flag
      setAccountTotals(data.totals || null);
      setDialogOpen(true);
    } catch (err: any) {
      console.error('Error fetching current accounts details:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoadingAccounts(false);
    }
  };

  const fetchPastDueAccountsDetails = async (page: number = 1, sortBy: string = 'days_past_due', sortOrder: string = 'DESC', searchLoan: string = '', searchCustomer: string = '') => {
    try {
      setLoadingAccounts(true);
      setError(null);
      setDialogType('past-due');
      
      const params: any = {
        page: page.toString(),
        page_size: '25',
        sort_by: sortBy,
        sort_order: sortOrder
      };
      if (searchLoan) params.search_loan_number = searchLoan;
      if (searchCustomer) params.search_customer_name = searchCustomer;
      
      const response = await apiClient.get('/port-health/past-due-accounts', { params });
      const data = response.data;
      
      setCurrentAccounts(data.accounts || []);
      setPagination(data.pagination || null);
      setSorting(data.sorting || { sort_by: 'days_past_due', sort_order: 'DESC' });
      setCurrentPage(page);
      setIsManagerAccounts(false); // Reset manager accounts flag
      setAccountTotals(data.totals || null);
      setDialogOpen(true);
    } catch (err: any) {
      console.error('Error fetching past due accounts details:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoadingAccounts(false);
    }
  };

  const fetchBucketAccountsDetails = async (minDays: number, maxDays?: number, page: number = 1, sortBy: string = 'days_past_due', sortOrder: string = 'DESC', searchLoan: string = '', searchCustomer: string = '') => {
    try {
      setLoadingAccounts(true);
      setError(null);
      setDialogType('bucket');
      setBucketInfo({ minDays, maxDays, name: maxDays ? `${minDays}-${maxDays} days` : `${minDays}+ days` });
      
      const params: any = {
        page: page.toString(),
        page_size: '25',
        sort_by: sortBy,
        sort_order: sortOrder
      };
      if (searchLoan) params.search_loan_number = searchLoan;
      if (searchCustomer) params.search_customer_name = searchCustomer;
      
      const endpoint = maxDays 
        ? `/port-health/past-due-bucket/${minDays}/${maxDays}`
        : `/port-health/past-due-bucket/${minDays}`;
      
      const response = await apiClient.get(endpoint, { params });
      const data = response.data;
      
      setCurrentAccounts(data.accounts || []);
      setPagination(data.pagination || null);
      setSorting(data.sorting || { sort_by: 'days_past_due', sort_order: 'DESC' });
      setCurrentPage(page);
      setIsManagerAccounts(false); // Reset manager accounts flag
      setAccountTotals(data.totals || null);
      setDialogOpen(true);
    } catch (err: any) {
      console.error('Error fetching bucket accounts details:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoadingAccounts(false);
    }
  };

  const fetchAllAccountsDetails = async (page: number = 1, sortBy: string = 'days_past_due', sortOrder: string = 'DESC', searchLoan: string = '', searchCustomer: string = '') => {
    try {
      setLoadingAccounts(true);
      setError(null);
      setDialogType('current');
      
      const params: any = {
        page: page.toString(),
        page_size: '25',
        sort_by: sortBy,
        sort_order: sortOrder
      };
      if (searchLoan) params.search_loan_number = searchLoan;
      if (searchCustomer) params.search_customer_name = searchCustomer;
      
      const response = await apiClient.get('/port-health/all-accounts', { params });
      const data = response.data;
      
      setCurrentAccounts(data.accounts || []);
      setPagination(data.pagination || null);
      setSorting(data.sorting || { sort_by: 'days_past_due', sort_order: 'DESC' });
      setCurrentPage(page);
      setIsManagerAccounts(false); // Reset manager accounts flag
      setAccountTotals(data.totals || null);
      setDialogOpen(true);
    } catch (err: any) {
      console.error('Error fetching all accounts details:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleCurrentAccountsClick = () => {
    fetchCurrentAccountsDetails();
  };

  const handlePastDueAccountsClick = () => {
    fetchPastDueAccountsDetails();
  };

  const fetchUnassignedAccountsDetails = async (
    welcomeCall: 'all' | 'yes' | 'no',
    page: number = 1,
    sortBy: string = 'loan_number',
    sortOrder: string = 'ASC',
    searchLoan: string = '',
    searchCustomer: string = ''
  ) => {
    try {
      setLoadingAccounts(true);
      setError(null);
      
      const params: any = {
        page: page.toString(),
        page_size: '25',
        sort_by: sortBy,
        sort_order: sortOrder,
        welcome_call: welcomeCall
      };
      if (searchLoan) params.search_loan_number = searchLoan;
      if (searchCustomer) params.search_customer_name = searchCustomer;

      const response = await apiClient.get('/port-health/unassigned-accounts', { params });
      const data = response.data;
      
      setUnassignedAccounts(data.accounts || []);
      setUnassignedDialogOpen(true);
    } catch (err: any) {
      console.error('Error fetching unassigned accounts details:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleUnassignedWelcomeYesClick = () => {
    fetchUnassignedAccountsDetails('yes');
  };

  const handleUnassignedWelcomeNoClick = () => {
    fetchUnassignedAccountsDetails('no');
  };

  const handlePageChange = (newPage: number) => {
    if (dialogType === 'current') {
      // Check if this is a manager account dialog
      if (isManagerAccounts && selectedManager) {
        fetchManagerAccountsDetails(selectedManager.userno, newPage, sorting.sort_by, sorting.sort_order, searchLoanNumber, searchCustomerName);
      }
      // Check if this is an officer account dialog
      else if (selectedOfficer) {
        if (bucketInfo) {
          // This is an officer bucket account dialog
          // Extract bucket from bucketInfo name (e.g., "11-30 days" -> "11-30")
          const bucketMatch = bucketInfo.name.match(/(\d+-\d+)/);
          if (bucketMatch) {
            const bucket = bucketMatch[1];
            fetchOfficerBucketAccounts(selectedOfficer.userno, bucket, newPage, sorting.sort_by, sorting.sort_order, searchLoanNumber, searchCustomerName);
          } else {
            fetchCurrentAccountsDetails(newPage, sorting.sort_by, sorting.sort_order, searchLoanNumber, searchCustomerName);
          }
        } else {
          // This is a regular officer account dialog
          fetchOfficerAccountsDetails(selectedOfficer.userno, newPage, sorting.sort_by, sorting.sort_order, searchLoanNumber, searchCustomerName);
        }
      } else {
        fetchCurrentAccountsDetails(newPage, sorting.sort_by, sorting.sort_order, searchLoanNumber, searchCustomerName);
      }
    } else if (dialogType === 'past-due') {
      fetchPastDueAccountsDetails(newPage, sorting.sort_by, sorting.sort_order, searchLoanNumber, searchCustomerName);
    } else if (dialogType === 'bucket' && bucketInfo) {
      fetchBucketAccountsDetails(bucketInfo.minDays, bucketInfo.maxDays, newPage, sorting.sort_by, sorting.sort_order, searchLoanNumber, searchCustomerName);
    }
  };

  const handleSortChange = (sortBy: string, sortOrder: string) => {
    // Update the sorting state immediately
    setSorting({ sort_by: sortBy, sort_order: sortOrder });
    
    if (dialogType === 'current') {
      // Check if this is a manager account dialog
      if (isManagerAccounts && selectedManager) {
        fetchManagerAccountsDetails(selectedManager.userno, 1, sortBy, sortOrder, searchLoanNumber, searchCustomerName);
      }
      // Check if this is an officer account dialog
      else if (selectedOfficer) {
        if (bucketInfo) {
          // This is an officer bucket account dialog
          // Extract bucket from bucketInfo name (e.g., "11-30 days" -> "11-30")
          const bucketMatch = bucketInfo.name.match(/(\d+-\d+)/);
          if (bucketMatch) {
            const bucket = bucketMatch[1];
            fetchOfficerBucketAccounts(selectedOfficer.userno, bucket, 1, sortBy, sortOrder, searchLoanNumber, searchCustomerName);
          } else {
            fetchCurrentAccountsDetails(1, sortBy, sortOrder, searchLoanNumber, searchCustomerName);
          }
        } else {
          // This is a regular officer account dialog
          fetchOfficerAccountsDetails(selectedOfficer.userno, 1, sortBy, sortOrder, searchLoanNumber, searchCustomerName);
        }
      } else {
        fetchCurrentAccountsDetails(1, sortBy, sortOrder, searchLoanNumber, searchCustomerName);
      }
    } else if (dialogType === 'past-due') {
      fetchPastDueAccountsDetails(1, sortBy, sortOrder, searchLoanNumber, searchCustomerName);
    } else if (dialogType === 'bucket' && bucketInfo) {
      fetchBucketAccountsDetails(bucketInfo.minDays, bucketInfo.maxDays, 1, sortBy, sortOrder, searchLoanNumber, searchCustomerName);
    }
  };

  const handleBucketClick = (minDays: number, maxDays?: number) => {
    fetchBucketAccountsDetails(minDays, maxDays);
  };

  // Debounced search function
  const debouncedSearch = React.useCallback((searchLoan: string, searchCustomer: string) => {
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set pending state
    setIsSearchPending(true);
    
    // Set new timeout
    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1); // Reset to first page when searching
      
      // Trigger search based on current dialog type
      if (dialogType === 'current') {
        // Check if this is a manager account dialog
        if (isManagerAccounts && selectedManager) {
          fetchManagerAccountsDetails(selectedManager.userno, 1, sorting.sort_by, sorting.sort_order, searchLoan, searchCustomer);
        }
        // Check if this is an officer account dialog
        else if (selectedOfficer) {
          if (bucketInfo) {
            // This is an officer bucket account dialog
            // Extract bucket from bucketInfo name (e.g., "11-30 days" -> "11-30")
            const bucketMatch = bucketInfo.name.match(/(\d+-\d+)/);
            if (bucketMatch) {
              const bucket = bucketMatch[1];
              fetchOfficerBucketAccounts(selectedOfficer.userno, bucket, 1, sorting.sort_by, sorting.sort_order, searchLoan, searchCustomer);
            } else {
              fetchCurrentAccountsDetails(1, sorting.sort_by, sorting.sort_order, searchLoan, searchCustomer);
            }
          } else {
            // This is a regular officer account dialog
            fetchOfficerAccountsDetails(selectedOfficer.userno, 1, sorting.sort_by, sorting.sort_order, searchLoan, searchCustomer);
          }
        } else {
          fetchCurrentAccountsDetails(1, sorting.sort_by, sorting.sort_order, searchLoan, searchCustomer);
        }
      } else if (dialogType === 'past-due') {
        fetchPastDueAccountsDetails(1, sorting.sort_by, sorting.sort_order, searchLoan, searchCustomer);
      } else if (dialogType === 'bucket' && bucketInfo) {
        fetchBucketAccountsDetails(bucketInfo.minDays, bucketInfo.maxDays, 1, sorting.sort_by, sorting.sort_order, searchLoan, searchCustomer);
      }
      
      // Clear pending state after search is initiated
      setIsSearchPending(false);
    }, 500); // 500ms delay - industry standard
  }, [dialogType, sorting.sort_by, sorting.sort_order, bucketInfo, selectedOfficer, selectedManager, isManagerAccounts]);

  const handleSearchChange = (searchLoan: string, searchCustomer: string) => {
    setSearchLoanNumber(searchLoan);
    setSearchCustomerName(searchCustomer);
    debouncedSearch(searchLoan, searchCustomer);
  };

  const handleSearchComplete = () => {
    // This will be called after focus is restored
    // No additional action needed, just a callback for the dialog
  };

  const handleExportAllData = async (): Promise<any[]> => {
    try {
      console.log('Starting export with dialogType:', dialogType, 'bucketInfo:', bucketInfo, 'selectedOfficer:', selectedOfficer);
      
      const params: any = {
        sort_by: sorting.sort_by,
        sort_order: sorting.sort_order
      };
      
      if (searchLoanNumber) params.search_loan_number = searchLoanNumber;
      if (searchCustomerName) params.search_customer_name = searchCustomerName;
      
      let endpoint = '';
      
      // Build endpoint based on dialog type
      if (dialogType === 'current') {
        if (isManagerAccounts && selectedManager) {
          endpoint = `/port-health/export/manager-accounts/${selectedManager.userno}`;
        } else if (selectedOfficer) {
          if (bucketInfo) {
            endpoint = `/port-health/export/officer-bucket-accounts/${selectedOfficer.userno}/${bucketInfo.name}`;
          } else {
            endpoint = `/port-health/export/officer-accounts/${selectedOfficer.userno}`;
          }
        } else {
          endpoint = `/port-health/export/bucket-accounts/0/0`;
        }
      } else if (dialogType === 'past-due') {
        endpoint = `/port-health/export/bucket-accounts/1/999`;
      } else if (dialogType === 'bucket' && bucketInfo) {
        const maxDays = bucketInfo.maxDays ?? 9999;
        endpoint = `/port-health/export/bucket-accounts/${bucketInfo.minDays}/${maxDays}`;
      } else {
        throw new Error(`Invalid dialog type for export: ${dialogType}`);
      }
      
      const response = await apiClient.get(endpoint, { params });
      const accounts = response.data.accounts || [];
      console.log('Export complete. Total accounts:', accounts.length);
      return accounts;
    } catch (err: any) {
      console.error('Error fetching all data for export:', err);
      throw err;
    }
  };

  const handlePieSliceClick = (data: any) => {
    console.log('Pie slice clicked:', data);
    if (data.name === 'Current') {
      console.log('Fetching current accounts details...');
      fetchCurrentAccountsDetails();
    } else if (data.name === 'Past Due') {
      console.log('Fetching past due accounts details...');
      fetchPastDueAccountsDetails();
    } else {
      console.log('Unknown pie slice clicked:', data.name);
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setCurrentAccounts([]);
    setBucketInfo(null);
    setSelectedOfficer(null);
    setIsManagerAccounts(false);
    setSearchLoanNumber('');
    setSearchCustomerName('');
    setCurrentPage(1);
    setIsSearchPending(false);
    // Clear any pending search timeouts
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
  };

  const fetchLoanDetails = async (loanNumber: string) => {
    try {
      setLoadingLoanDetails(true);
      setLoanDetailsError(null);
      setLoanDetailsDialogOpen(true);
      
      const response = await apiClient.get(`/port-health/loan-details/${encodeURIComponent(loanNumber)}`);
      const data = response.data;
      
      if (data.error) {
        setLoanDetailsError(data.error);
        setLoanDetails(null);
      } else {
        setLoanDetails(data);
        setLoanDetailsError(null);
      }
    } catch (err: any) {
      console.error('Error fetching loan details:', err);
      setLoanDetailsError(getErrorMessage(err));
      setLoanDetails(null);
    } finally {
      setLoadingLoanDetails(false);
    }
  };

  const handleLoanNumberClick = (loanNumber: string) => {
    fetchLoanDetails(loanNumber);
  };

  const closeLoanDetailsDialog = () => {
    setLoanDetailsDialogOpen(false);
    setLoanDetails(null);
    setLoanDetailsError(null);
  };

  // Manager/Officer functions
  const fetchManagers = async () => {
    try {
      setLoadingManagers(true);
      const response = await apiClient.get('/port-health/managers');
const data = response.data;

const managersPayload = Array.isArray(data)
  ? data
  : data?.managers || data?.data || [];

if (!Array.isArray(managersPayload)) {
  const msg =
    typeof data === 'object' && data && 'error' in data
      ? String((data as any).error)
      : 'Unexpected response from /port-health/managers';

  console.error('Unexpected managers payload:', data);
  setManagers([]);
  setManagerMetrics({});
  setError(msg);
  return;
}

setManagers(managersPayload);
      
      // Fetch metrics for each manager
      const metricsPromises = managersPayload.map(async (manager: Manager) => {
        try {
          const metricsResponse = await apiClient.get(`/port-health/manager-metrics/${manager.userno}`);
          if (metricsResponse.status === 200) {
            return { userno: manager.userno, metrics: metricsResponse.data };
          }
        } catch (err) {
          console.error(`Error fetching metrics for manager ${manager.userno}:`, err);
        }
        return null;
      });
      
      const metricsResults = await Promise.all(metricsPromises);
      const metricsMap: {[key: number]: ManagerMetrics} = {};
      metricsResults.forEach(result => {
        if (result) {
          metricsMap[result.userno] = result.metrics;
        }
      });
      setManagerMetrics(metricsMap);
      
    } catch (err: any) {
      console.error('Error fetching managers:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoadingManagers(false);
    }
  };

  const fetchCollectionOfficers = async (managerUserno: number) => {
    try {
      setLoadingOfficers(true);
      const response = await apiClient.get(`/port-health/collection-officers/${managerUserno}`);
      const data = response.data;
      if (!Array.isArray(data)) {
        const msg =
          typeof data === 'object' && data && 'error' in data
            ? String((data as any).error)
            : 'Unexpected response from /port-health/collection-officers';
        console.error('Unexpected officers payload:', data);
        setOfficers([]);
        setOfficerMetrics({});
        setError(msg);
        return;
      }
      setOfficers(data);
      
      // Fetch metrics for each officer
      const metricsPromises = data.map(async (officer: CollectionOfficer) => {
        try {
          const metricsResponse = await apiClient.get(`/port-health/manager-metrics/${officer.userno}`);
          if (metricsResponse.status === 200) {
            return { userno: officer.userno, metrics: metricsResponse.data };
          }
        } catch (err) {
          console.error(`Error fetching metrics for officer ${officer.userno}:`, err);
        }
        return null;
      });
      
      const metricsResults = await Promise.all(metricsPromises);
      const metricsMap: {[key: number]: ManagerMetrics} = {};
      metricsResults.forEach(result => {
        if (result) {
          metricsMap[result.userno] = result.metrics;
        }
      });
      setOfficerMetrics(metricsMap);
      
    } catch (err: any) {
      console.error('Error fetching collection officers:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoadingOfficers(false);
    }
  };

  const fetchOfficerAccountsDetails = async (officerUserno: number, page: number = 1, sortBy: string = 'days_past_due', sortOrder: string = 'DESC', searchLoan: string = '', searchCustomer: string = '') => {
    try {
      setLoadingAccounts(true);
      setError(null);
      
      const officerClient = createApiClient(30000); // 30 second timeout
      
      const params: any = {
        page: page.toString(),
        page_size: '25',
        sort_by: sortBy,
        sort_order: sortOrder
      };
      if (searchLoan) params.search_loan_number = searchLoan;
      if (searchCustomer) params.search_customer_name = searchCustomer;
      
      const response = await officerClient.get(`/port-health/officer-accounts/${officerUserno}`, { params });
      const data = response.data;
      
      setCurrentAccounts(data.accounts || []);
      setPagination(data.pagination || null);
      setSorting(data.sorting || { sort_by: 'days_past_due', sort_order: 'DESC' });
      setCurrentPage(page);
      setDialogType('current'); // Use 'current' type for officer accounts
      setIsManagerAccounts(false); // Reset manager accounts flag
      setAccountTotals(data.totals || null);
      setDialogOpen(true);
      
    } catch (err: any) {
      console.error('Error fetching officer accounts:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoadingAccounts(false);
    }
  };

  const fetchManagerAccountsDetails = async (managerUserno: number, page: number = 1, sortBy: string = 'days_past_due', sortOrder: string = 'DESC', searchLoan: string = '', searchCustomer: string = '') => {
    try {
      setLoadingAccounts(true);
      setError(null);
      
      const managerClient = createApiClient(30000); // 30 second timeout
      
      const params: any = {
        page: page.toString(),
        page_size: '25',
        sort_by: sortBy,
        sort_order: sortOrder
      };
      if (searchLoan) params.search_loan_number = searchLoan;
      if (searchCustomer) params.search_customer_name = searchCustomer;
      
      const response = await managerClient.get(`/port-health/manager-accounts/${managerUserno}`, { params });
      const data = response.data;
      
      setCurrentAccounts(data.accounts || []);
      setPagination(data.pagination || null);
      setSorting(data.sorting || { sort_by: 'days_past_due', sort_order: 'DESC' });
      setCurrentPage(page);
      setDialogType('current'); // Use 'current' type for manager accounts
      setIsManagerAccounts(true); // Mark this as manager accounts
      setAccountTotals(data.totals || null);
      setDialogOpen(true);
      
    } catch (err: any) {
      console.error('Error fetching manager accounts:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoadingAccounts(false);
    }
  };

  const fetchOfficerBucketAccounts = async (officerUserno: number, bucket: string, page: number = 1, sortBy: string = 'days_past_due', sortOrder: string = 'DESC', searchLoan: string = '', searchCustomer: string = '') => {
    try {
      setLoadingAccounts(true);
      setError(null);
      
      const bucketClient = createApiClient(30000); // 30 second timeout

      const params: any = {
        page: page.toString(),
        page_size: '25',
        sort_by: sortBy,
        sort_order: sortOrder
      };
      if (searchLoan) params.search_loan_number = searchLoan;
      if (searchCustomer) params.search_customer_name = searchCustomer;

      const response = await bucketClient.get(`/port-health/officer-bucket-accounts/${officerUserno}/${bucket}`, { params });
      const data = response.data;
      
      setCurrentAccounts(data.accounts || []);
      setPagination(data.pagination || null);
      setSorting(data.sorting || { sort_by: 'days_past_due', sort_order: 'DESC' });
      setCurrentPage(page);
      setBucketInfo({ minDays: 0, maxDays: 0, name: bucket }); // Set bucket info for officer bucket accounts
      setDialogType('current'); // Use 'current' type for bucket accounts
      setIsManagerAccounts(false); // Reset manager accounts flag
      setAccountTotals(data.totals || null);
      setDialogOpen(true);
      
    } catch (err: any) {
      console.error('Error fetching officer bucket accounts:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleManagerClick = (manager: Manager) => {
    setSelectedManager(manager);
    setViewMode('officers');
    fetchCollectionOfficers(manager.userno);
  };

  const handleViewAllManagerAccounts = async (manager: Manager) => {
    setSelectedManager(manager);
    await fetchManagerAccountsDetails(manager.userno, 1, sorting.sort_by, sorting.sort_order, searchLoanNumber, searchCustomerName);
  };

  const handleOfficerClick = async (officer: CollectionOfficer) => {
    setSelectedOfficer(officer);
    await fetchOfficerAccountsDetails(officer.userno, 1, sorting.sort_by, sorting.sort_order, searchLoanNumber, searchCustomerName);
  };

  const handleOfficerBucketClick = async (officer: CollectionOfficer, bucket: string) => {
    setSelectedOfficer(officer);
    await fetchOfficerBucketAccounts(officer.userno, bucket, 1, sorting.sort_by, sorting.sort_order, searchLoanNumber, searchCustomerName);
  };

  const handleTotalPortfolioBucketClick = async (bucket: string) => {
    setSelectedOfficer(null);
    
    // Handle special cases for '30+' and '120+' buckets
    if (bucket === '120+') {
      await fetchBucketAccountsDetails(121, 9999, 1, sorting.sort_by, sorting.sort_order, searchLoanNumber, searchCustomerName);
    } else if (bucket === '30+') {
      await fetchBucketAccountsDetails(31, 9999, 1, sorting.sort_by, sorting.sort_order, searchLoanNumber, searchCustomerName);
    } else {
      // Regular buckets like '31-60', '61-90', etc.
      await fetchBucketAccountsDetails(parseInt(bucket.split('-')[0]), parseInt(bucket.split('-')[1]) || undefined, 1, sorting.sort_by, sorting.sort_order, searchLoanNumber, searchCustomerName);
    }
  };

  const handleViewAllTotalPortfolioAccounts = async () => {
    setSelectedOfficer(null);
    await fetchAllAccountsDetails(1, sorting.sort_by, sorting.sort_order, searchLoanNumber, searchCustomerName);
  };

  const handleViewModeChange = (mode: 'overview' | 'managers' | 'officers') => {
    setViewMode(mode);
    if (mode === 'managers' && managers.length === 0) {
      fetchManagers();
    }
  };

  const handleBackToManagers = () => {
    setViewMode('managers');
    setSelectedManager(null);
    setOfficers([]);
    setOfficerMetrics({});
  };

  const handleBackToOverview = () => {
    setViewMode('overview');
    setSelectedManager(null);
    setOfficers([]);
    setOfficerMetrics({});
  };

  if (loading) {
    return (
      <div className="port-health-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading portfolio health metrics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="port-health-container">
        <div className="error-message">
          <h2>⚠️ Error</h2>
          <p>{error}</p>
          <button onClick={fetchPortfolioHealth} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="port-health-container">
        <div className="error-message">
          <p>No data available</p>
        </div>
      </div>
    );
  }

  // Data for pie chart
  const pieData = [
    { name: 'Current', value: metrics.currentLoans ?? 0, percentage: metrics.pctCurrent ?? 0 },
    { name: 'Past Due', value: metrics.pastDueLoans ?? 0, percentage: metrics.pctPastDue ?? 0 }
  ];

  // Data for bar chart
  const barData = [
    { 
      name: 'Portfolio Status', 
      Current: metrics.currentLoans ?? 0, 
      'Past Due': metrics.pastDueLoans ?? 0 
    }
  ];

  const COLORS = {
    current: '#3b82f6',    // Blue for current
    pastDue: '#eab308'     // Yellow for past due
  };

  // Custom label for pie chart
  const renderCustomLabel = ({ name, percentage }: any) => {
    return `${name}: ${percentage.toFixed(2)}%`;
  };

  return (
    <div className={`port-health-container ${isLoadingCursor ? 'loading-cursor' : ''}`}>
      <div className="port-health-header">
        <h1>💚 Portfolio Health Monitoring <span className="version-watermark">v1.0.0</span></h1>
        <p className="subtitle">Track current vs delinquent loans across the active portfolio</p>
        
        {/* Portfolio Health Status - Simple */}
        <div className="simple-health-status">
          <div className="health-bar-container">
            <div 
              className="health-bar-current" 
              style={{ width: `${metrics.pctCurrent ?? 0}%` }}
            >
              <span>{(metrics.pctCurrent ?? 0).toFixed(1)}% Current</span>
            </div>
            <div 
              className="health-bar-past-due" 
              style={{ width: `${metrics.pctPastDue ?? 0}%` }}
            >
              <span>{(metrics.pctPastDue ?? 0).toFixed(1)}% Past Due</span>
            </div>
          </div>
          {(metrics.pctCurrent ?? 100) < 70 && (
            <div className="health-alert">
              <span className="alert-icon">⚠️</span>
              <span>Alert! Significant portion of portfolio past due.</span>
            </div>
          )}
        </div>
        
        {/* Debug components removed - profile pictures are now working! */}
        
        <div className="header-actions">
          <button onClick={fetchPortfolioHealth} className="refresh-button">
            🔄 Refresh Data
          </button>
        </div>
      </div>

      {/* Unified Navigation breadcrumb */}
      <div className="breadcrumb">
        <button 
          onClick={() => handleViewModeChange('overview')} 
          className={`breadcrumb-link ${viewMode === 'overview' ? 'active' : ''}`}
        >
          📊 Portfolio Overview
        </button>
        
        {viewMode === 'overview' && (
          <>
            <span className="breadcrumb-separator">›</span>
            <button 
              onClick={() => handleViewModeChange('managers')} 
              className="breadcrumb-link"
            >
              👥 Managers
            </button>
          </>
        )}
        
        {viewMode === 'managers' && (
          <>
            <span className="breadcrumb-separator">›</span>
            <span className="breadcrumb-current">
              👥 Managers
            </span>
          </>
        )}
        
        {viewMode === 'officers' && selectedManager && (
          <>
            <span className="breadcrumb-separator">›</span>
            <button onClick={handleBackToManagers} className="breadcrumb-link">
              👥 Managers
            </button>
            <span className="breadcrumb-separator">›</span>
            <span className="breadcrumb-current">
              💼 {selectedManager.full_name}'s Officers
            </span>
          </>
        )}
      </div>

      {/* Main Content - Conditional Rendering */}
      {viewMode === 'overview' && (
        <>

          {/* Summary Cards */}
          <div className="metrics-cards">
            <div className="metric-card unassigned">
              <div className="metric-icon">📞</div>
              <div className="metric-content">
                <h3>Unassigned Accounts</h3>
                <p className="metric-value">
                  {loadingUnassigned
                    ? 'Loading...'
                    : (unassignedSummary?.total_unassigned ?? 0).toLocaleString()}
                </p>
                <div className="unassigned-links">
                  <button
                    type="button"
                    className="link-button"
                    onClick={handleUnassignedWelcomeYesClick}
                    disabled={
                      loadingUnassigned ||
                      (unassignedSummary?.yes_welcome_call ?? 0) === 0
                    }
                  >
                    Welcome Call Made (
                    {(unassignedSummary?.yes_welcome_call ?? 0).toLocaleString()}
                    )
                  </button>
                  <button
                    type="button"
                    className="link-button"
                    onClick={handleUnassignedWelcomeNoClick}
                    disabled={
                      loadingUnassigned ||
                      (unassignedSummary?.no_welcome_call ?? 0) === 0
                    }
                  >
                    No Welcome Call (
                    {(unassignedSummary?.no_welcome_call ?? 0).toLocaleString()}
                    )
                  </button>
                </div>
              </div>
            </div>

            <div className="metric-card total">
              <div className="metric-icon">📊</div>
              <div className="metric-content">
                <h3>Total Active Loans</h3>
                <p className="metric-value">{(metrics.totalLoans ?? 0).toLocaleString()}</p>
              </div>
            </div>

            <div className="metric-card current clickable" onClick={handleCurrentAccountsClick}>
              <div className="metric-icon">✅</div>
              <div className="metric-content">
                <h3>Current Loans</h3>
                <p className="metric-value">{(metrics.currentLoans ?? 0).toLocaleString()}</p>
                <p className="metric-percentage">{(metrics.pctCurrent ?? 0).toFixed(2)}%</p>
                <p className="click-hint">Click to view details</p>
              </div>
            </div>

            <div className="metric-card past-due clickable" onClick={handlePastDueAccountsClick}>
              <div className="metric-icon">⚠️</div>
              <div className="metric-content">
                <h3>Past Due Loans</h3>
                <p className="metric-value">{(metrics.pastDueLoans ?? 0).toLocaleString()}</p>
                <p className="metric-percentage">{(metrics.pctPastDue ?? 0).toFixed(2)}%</p>
                <p className="click-hint">Click to view details</p>
              </div>
            </div>
          </div>

      {/* Past Due Buckets */}
      <div className="metrics-cards buckets">
        <div className="metric-card bucket-1-10 clickable" onClick={() => handleBucketClick(1, 10)}>
          <div className="metric-icon">🟢</div>
          <div className="metric-content">
            <h3>1-10 Days</h3>
            <p className="metric-value">
              {(metrics.oneTo10 ?? 0).toLocaleString()}
              {metrics.trends && (
                <span 
                  className={`trend-indicator ${metrics.trends.oneTo10}`}
                  title={metrics.trends.oneTo10 === 'green' ? 'Increase < 10%' : 
                         metrics.trends.oneTo10 === 'orange' ? 'Increase 10-20%' : 'Increase ≥ 20%'}
                ></span>
              )}
            </p>
            <p className="metric-percentage">{(metrics.pctOneTo10 ?? 0).toFixed(2)}%</p>
            <p className="click-hint">Click to view details</p>
          </div>
        </div>

        <div className="metric-card bucket-11-30 clickable" onClick={() => handleBucketClick(11, 30)}>
          <div className="metric-icon">🟡</div>
          <div className="metric-content">
            <h3>11-30 Days</h3>
            <p className="metric-value">
              {(metrics.elevenTo30 ?? 0).toLocaleString()}
              {metrics.trends && (
                <span 
                  className={`trend-indicator ${metrics.trends.elevenTo30}`}
                  title={metrics.trends.elevenTo30 === 'green' ? 'Increase < 10%' : 
                         metrics.trends.elevenTo30 === 'orange' ? 'Increase 10-20%' : 'Increase ≥ 20%'}
                ></span>
              )}
            </p>
            <p className="metric-percentage">{(metrics.pctElevenTo30 ?? 0).toFixed(2)}%</p>
            <p className="click-hint">Click to view details</p>
          </div>
        </div>

        <div className="metric-card bucket-31-60 clickable" onClick={() => handleBucketClick(31, 60)}>
          <div className="metric-icon">🟠</div>
          <div className="metric-content">
            <h3>31-60 Days</h3>
            <p className="metric-value">
              {(metrics.thirtyOneTo60 ?? 0).toLocaleString()}
              {metrics.trends && (
                <span 
                  className={`trend-indicator ${metrics.trends.thirtyOneTo60}`}
                  title={metrics.trends.thirtyOneTo60 === 'green' ? 'Increase < 10%' : 
                         metrics.trends.thirtyOneTo60 === 'orange' ? 'Increase 10-20%' : 'Increase ≥ 20%'}
                ></span>
              )}
            </p>
            <p className="metric-percentage">{(metrics.pctThirtyOneTo60 ?? 0).toFixed(2)}%</p>
            <p className="click-hint">Click to view details</p>
          </div>
        </div>

        <div className="metric-card bucket-61-90 clickable" onClick={() => handleBucketClick(61, 90)}>
          <div className="metric-icon">🔴</div>
          <div className="metric-content">
            <h3>61-90 Days</h3>
            <p className="metric-value">
              {(metrics.sixtyOneTo90 ?? 0).toLocaleString()}
              {metrics.trends && (
                <span 
                  className={`trend-indicator ${metrics.trends.sixtyOneTo90}`}
                  title={metrics.trends.sixtyOneTo90 === 'green' ? 'Increase < 10%' : 
                         metrics.trends.sixtyOneTo90 === 'orange' ? 'Increase 10-20%' : 'Increase ≥ 20%'}
                ></span>
              )}
            </p>
            <p className="metric-percentage">{(metrics.pctSixtyOneTo90 ?? 0).toFixed(2)}%</p>
            <p className="click-hint">Click to view details</p>
          </div>
        </div>

        <div className="metric-card bucket-91-120 clickable" onClick={() => handleBucketClick(91, 120)}>
          <div className="metric-icon">🟣</div>
          <div className="metric-content">
            <h3>91-120 Days</h3>
            <p className="metric-value">
              {(metrics.ninetyOneTo120 ?? 0).toLocaleString()}
              {metrics.trends && (
                <span 
                  className={`trend-indicator ${metrics.trends.ninetyOneTo120}`}
                  title={metrics.trends.ninetyOneTo120 === 'green' ? 'Increase < 10%' : 
                         metrics.trends.ninetyOneTo120 === 'orange' ? 'Increase 10-20%' : 'Increase ≥ 20%'}
                ></span>
              )}
            </p>
            <p className="metric-percentage">{(metrics.pctNinetyOneTo120 ?? 0).toFixed(2)}%</p>
            <p className="click-hint">Click to view details</p>
          </div>
        </div>

        <div className="metric-card bucket-120-plus clickable" onClick={() => handleBucketClick(121)}>
          <div className="metric-icon">⚫</div>
          <div className="metric-content">
            <h3>120+ Days</h3>
            <p className="metric-value">
              {(metrics.oneTwentyOnePlus ?? 0).toLocaleString()}
              {metrics.trends && (
                <span 
                  className={`trend-indicator ${metrics.trends.oneTwentyOnePlus}`}
                  title={metrics.trends.oneTwentyOnePlus === 'green' ? 'Increase < 10%' : 
                         metrics.trends.oneTwentyOnePlus === 'orange' ? 'Increase 10-20%' : 'Increase ≥ 20%'}
                ></span>
              )}
            </p>
            <p className="metric-percentage">{(metrics.pctOneTwentyOnePlus ?? 0).toFixed(2)}%</p>
            <p className="click-hint">Click to view details</p>
          </div>
        </div>

        <div className="metric-card bucket-30-plus clickable" onClick={() => handleBucketClick(31)}>
          <div className="metric-icon">🔴</div>
          <div className="metric-content">
            <h3>30+ Days</h3>
            <p className="metric-value">
              {(metrics.thirtyPlus ?? 0).toLocaleString()}
              {metrics.trends && (
                <span 
                  className={`trend-indicator ${metrics.trends.thirtyPlus}`}
                  title={metrics.trends.thirtyPlus === 'green' ? 'Increase < 10%' : 
                         metrics.trends.thirtyPlus === 'orange' ? 'Increase 10-20%' : 'Increase ≥ 20%'}
                ></span>
              )}
            </p>
            <p className="metric-percentage">{(metrics.pctThirtyPlus ?? 0).toFixed(2)}%</p>
            <p className="click-hint">Click to view details</p>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-container">
        {/* Pie Chart */}
        <div className="chart-card">
          <h2>Portfolio Distribution</h2>
          <ResponsiveContainer width="100%" height={350}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomLabel}
                outerRadius={120}
                fill="#8884d8"
                dataKey="value"
                onClick={handlePieSliceClick}
              >
                <Cell fill={COLORS.current} />
                <Cell fill={COLORS.pastDue} />
              </Pie>
              <Tooltip formatter={(value: number) => value.toLocaleString()} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Bar Chart */}
        <div className="chart-card">
          <h2>Loan Status Comparison</h2>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={barData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value: number) => value.toLocaleString()} />
              <Legend />
              <Bar dataKey="Current" fill={COLORS.current} />
              <Bar dataKey="Past Due" fill={COLORS.pastDue} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

        </>
      )}

      {/* Managers View */}
      {viewMode === 'managers' && (
        <div className="managers-view">
          <div className="view-header">
            <h2>👥 Collection Managers</h2>
            <p>Click on a manager to view their collection officers</p>
          </div>
          
          {loadingManagers ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading managers...</p>
            </div>
          ) : (
            <div className="managers-grid">
              <TotalPortfolioCard
                metrics={{
                  total_loans: metrics?.totalLoans || 0,
                  current_loans: metrics?.currentLoans || 0,
                  past_due_loans: metrics?.pastDueLoans || 0,
                  one_to_10: metrics?.oneTo10 || 0,
                  eleven_to_30: metrics?.elevenTo30 || 0,
                  thirty_one_to_60: metrics?.thirtyOneTo60 || 0,
                  sixty_one_to_90: metrics?.sixtyOneTo90 || 0,
                  ninety_one_to_120: metrics?.ninetyOneTo120 || 0,
                  one_twenty_one_plus: metrics?.oneTwentyOnePlus || 0,
                  thirty_plus: metrics?.thirtyPlus || 0,
                  total_balance: metrics?.totalBalance || 0,
                  pct_current: metrics?.pctCurrent || 0,
                  pct_past_due: metrics?.pctPastDue || 0,
                  pct_one_to_10: metrics?.pctOneTo10 || 0,
                  pct_eleven_to_30: metrics?.pctElevenTo30 || 0,
                  pct_thirty_one_to_60: metrics?.pctThirtyOneTo60 || 0,
                  pct_sixty_one_to_90: metrics?.pctSixtyOneTo90 || 0,
                  pct_ninety_one_to_120: metrics?.pctNinetyOneTo120 || 0,
                  pct_one_twenty_one_plus: metrics?.pctOneTwentyOnePlus || 0,
                  pct_thirty_plus: metrics?.pctThirtyPlus || 0,
                  trends: metrics?.trends
                }}
                onViewAllAccounts={handleViewAllTotalPortfolioAccounts}
                onBucketClick={handleTotalPortfolioBucketClick}
                loading={loading}
              />
              {managers.map((manager) => (
                <ManagerCard
                  key={manager.userno}
                  manager={manager}
                  metrics={managerMetrics[manager.userno] || {
                    total_loans: 0,
                    current_loans: 0,
                    past_due_loans: 0,
                    one_to_10: 0,
                    eleven_to_30: 0,
                    thirty_one_to_60: 0,
                    sixty_one_to_90: 0,
                    ninety_one_to_120: 0,
                    one_twenty_one_plus: 0,
                    thirty_plus: 0,
                    total_balance: 0,
                    pct_current: 0,
                    pct_past_due: 0,
                    pct_one_to_10: 0,
                    pct_eleven_to_30: 0,
                    pct_thirty_one_to_60: 0,
                    pct_sixty_one_to_90: 0,
                    pct_ninety_one_to_120: 0,
                    pct_one_twenty_one_plus: 0,
                    pct_thirty_plus: 0
                  }}
                  onManagerClick={handleManagerClick}
                  onViewAllAccounts={handleViewAllManagerAccounts}
                  loading={loadingManagers}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Officers View */}
      {viewMode === 'officers' && selectedManager && (
        <div className="officers-view">
          <div className="view-header">
            <h2>💼 Collection Officers - {selectedManager.full_name}</h2>
            <p>Click on an officer to view their detailed accounts</p>
          </div>
          
          {loadingOfficers ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading collection officers...</p>
            </div>
          ) : (
            <div className="officers-grid">
              {officers.map((officer) => (
                <CollectionOfficerCard
                  key={officer.userno}
                  officer={officer}
                  metrics={officerMetrics[officer.userno] || {
                    total_loans: 0,
                    current_loans: 0,
                    past_due_loans: 0,
                    one_to_10: 0,
                    eleven_to_30: 0,
                    thirty_one_to_60: 0,
                    sixty_one_to_90: 0,
                    ninety_one_to_120: 0,
                    one_twenty_one_plus: 0,
                    thirty_plus: 0,
                    total_balance: 0,
                    pct_current: 0,
                    pct_past_due: 0,
                    pct_one_to_10: 0,
                    pct_eleven_to_30: 0,
                    pct_thirty_one_to_60: 0,
                    pct_sixty_one_to_90: 0,
                    pct_ninety_one_to_120: 0,
                    pct_one_twenty_one_plus: 0,
                    pct_thirty_plus: 0
                  }}
                  onOfficerClick={handleOfficerClick}
                  onBucketClick={handleOfficerBucketClick}
                  loading={false}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Account Details Dialog */}
      <AccountDetailsDialog
        isOpen={dialogOpen}
        onClose={closeDialog}
        accounts={currentAccounts}
        title={
          selectedOfficer ? `${selectedOfficer.full_name} - Accounts Details` :
          dialogType === 'current' ? (selectedOfficer ? 'Current Accounts Details' : 'Total Portfolio - All Accounts') :
          dialogType === 'past-due' ? 'Past Due Accounts Details' :
          bucketInfo ? `${bucketInfo.name} Accounts Details` : 'Account Details'
        }
        loading={loadingAccounts}
        onLoanNumberClick={handleLoanNumberClick}
        pagination={pagination || undefined}
        sorting={sorting}
        onPageChange={handlePageChange}
        onSortChange={handleSortChange}
        onSearchChange={handleSearchChange}
        searchLoanNumber={searchLoanNumber}
        searchCustomerName={searchCustomerName}
        isSearchPending={isSearchPending}
        onSearchComplete={handleSearchComplete}
        onExportAllData={handleExportAllData}
        totals={accountTotals || undefined}
      />

      {/* Loan Details Dialog */}
      <LoanDetailsDialog
        isOpen={loanDetailsDialogOpen}
        onClose={closeLoanDetailsDialog}
        loanDetails={loanDetails}
        loading={loadingLoanDetails}
        error={loanDetailsError}
      />

      {/* Unassigned Accounts Dialog */}
      <UnassignedAccountsDialog
        isOpen={unassignedDialogOpen}
        onClose={() => {
          setUnassignedDialogOpen(false);
          setUnassignedAccounts([]);
        }}
        accounts={unassignedAccounts}
        loading={loadingAccounts}
      />
    </div>
  );
};

export default PortHealth;

